import { useMemo } from "react";
import { shortAddr } from "../../wallets/lib/format";
import { ApiDown, Chip, RebuildNote, Empty, GridNote, OutcomeLead, OutcomeTag, Panel, SearchBox, Select, SourceError, WalletLink } from "../components/Bits";
import { type Col, DataGrid } from "../components/DataGrid";
import { View } from "../components/Shell";
import { ViewHeader } from "../components/ViewHeader";
import { FLAGS_REBUILDING } from "../lib/flags";
import { isoSeconds } from "../lib/csv";
import { FLAGS_REFRESH_MS, useFlaggedFeed } from "../lib/data";
import { Updated } from "../lib/freshness";
import { cents, count, stamp, toneClass, usd } from "../lib/format";
import { type FlaggedRow, ruleText } from "../lib/flagged";
import { categoryOptions, matches } from "../lib/markets";
import { openMarket, useParam, useSearchParam } from "../lib/nav";
import { useApiStatus } from "../lib/status";

const Z_OPTIONS = [
	{ value: "", label: "Z: any" },
	{ value: "2", label: "Z ≥ 2" },
	{ value: "3", label: "Z ≥ 3" },
	{ value: "5", label: "Z ≥ 5" },
	{ value: "10", label: "Z ≥ 10" },
];

const STATUS_LABEL = { for: "resolved ✓", against: "resolved ✗", open: "open" } as const;
const STATUS_TITLE = "✓ the market resolved for the flagged outcome, ✗ against it. A flag is not a prediction, so neither is a hit or a miss.";

const COLS: Col<FlaggedRow>[] = [
	{ key: "t", label: "Detected", title: "When the position was flagged, in your timezone", w: 80, prio: 3, sort: (r) => r.time, csv: (r) => isoSeconds(r.time), cell: (r) => <span className="text-dim">{stamp(r.time)}</span> },
	{ key: "q", label: "Market", w: 150, grow: 3, sort: (r) => r.question, cell: (r, narrow) => <span className={narrow ? "clamp-2 text-bright" : "text-bright"} title={r.question}>{narrow ? <OutcomeLead name={r.outcome} /> : null}{r.question}</span> },
	{ key: "o", label: "Outcome", w: 56, prio: 2, csv: (r) => r.outcome, cell: (r) => <OutcomeTag name={r.outcome} /> },
	{
		key: "z",
		label: "Z",
		title: "Position size in standard deviations above the token's mean trade. n/a when that market's stats are not in the loaded set.",
		w: 46,
		right: true,
		prio: 4,
		sort: (r) => r.z,
		cell: (r) => <span className={r.z !== null ? "font-bold text-flag" : "text-mute"} title={r.zWhy ?? undefined}>{r.z === null ? "n/a" : r.z.toFixed(1)}</span>,
	},
	{ key: "w", label: "Wallet", w: 88, prio: 1, sort: (r) => r.wallet, cell: (r) => <WalletLink address={r.wallet} label={shortAddr(r.wallet)} /> },
	{ key: "v", label: "Size", title: "Dollars in the flagged position", w: 56, right: true, sort: (r) => r.volume, cell: (r) => <span className="font-bold text-bright">{usd(r.volume)}</span> },
	{
		key: "p",
		label: "Entry → Now",
		title: "Price of the flagged outcome when it was flagged, then its latest price",
		w: 86,
		right: true,
		prio: 5,
		sort: (r) => r.now - r.entry,
		cell: (r) => <span>{cents(r.entry)} → <span className={toneClass(r.now - r.entry)}>{cents(r.now)}</span></span>,
	},
	{ key: "s", label: "Status", title: STATUS_TITLE, w: 66, prio: 6, sort: (r) => r.status, cell: (r) => <Chip title={STATUS_TITLE}>{STATUS_LABEL[r.status]}</Chip> },
	{ key: "m", label: "Mkts", title: "Markets this wallet has traded", w: 38, right: true, prio: 7, sort: (r) => r.markets, cell: (r) => count(r.markets) },
	{ key: "c", label: "Category", w: 70, right: true, prio: 8, sort: (r) => r.category, cell: (r) => <span className="text-dim">{r.category ?? "–"}</span> },
];

export function FlaggedView() {
	const api = useApiStatus();
	const [q, setQ] = useSearchParam();
	const [minZ, setMinZ] = useParam("z");
	const [category, setCategory] = useParam("cat", "all");
	const feed = useFlaggedFeed(minZ ? Number(minZ) : undefined);

	const categories = useMemo(() => {
		const names = [...new Set(feed.rows.map((r) => r.category).filter((c): c is string => Boolean(c)))].sort();
		return categoryOptions(names, category);
	}, [feed.rows, category]);

	const shown = useMemo(
		() => feed.rows.filter((r) => (category === "all" || r.category === category) && matches(q, r.question, r.wallet)),
		[feed.rows, q, category],
	);

	const noZ = useMemo(() => {
		const why = new Map<string, number>();
		for (const r of shown) if (r.zWhy) why.set(r.zWhy, (why.get(r.zWhy) ?? 0) + 1);
		return [...why.entries()].map(([text, n]) => `${count(n)} rows, ${text}`).join("; ");
	}, [shown]);
	const down = api.state === "down" && feed.rows.length === 0;
	const body = down ? (
		<ApiDown what="flagged positions" />
	) : feed.error && feed.rows.length === 0 ? (
		<SourceError source="api.polygains.com" onRetry={api.recheck} />
	) : !feed.isLoading && shown.length === 0 ? (
		<Empty>{FLAGS_REBUILDING ? "No flags yet: the rebuild is still running." : "No flagged positions match these filters."}</Empty>
	) : undefined;

	return (
		<View>
			<ViewHeader
				title="flagged positions"
				meta={!FLAGS_REBUILDING && feed.total !== null ? `${count(feed.total)} flagged positions` : undefined}
				desc={ruleText(feed.admission)}
			>
				<SearchBox value={q} onChange={setQ} placeholder="search market, 0x" />
				<Select label="Category" value={category} options={categories} onChange={setCategory} />
				<Select label="Minimum Z" value={minZ} options={Z_OPTIONS} onChange={setMinZ} />
			</ViewHeader>
			<RebuildNote />
			<Panel>
				<DataGrid
					label="Flagged positions"
					cols={COLS}
					rows={shown}
					rowKey={(r) => r.id}
					initialSort={{ key: "t", dir: "desc" }}
					csvName="flagged"
					copyValue={(r) => r.wallet}
					onRow={(r) => r.conditionId && openMarket(r.conditionId)}
					loading={!down && (feed.isLoading || api.state === "checking") && feed.rows.length === 0}
					body={body}
				/>
				<GridNote className="min-h-[75px] sm:min-h-0">
					<Updated at={feed.updatedAt} refreshMs={FLAGS_REFRESH_MS} failing={Boolean(feed.error) || api.state === "down"} />
					{feed.rows.length ? `${count(feed.rows.length)} loaded of ${count(feed.total)}, newest first · ` : ""}source: PolyGains /alerts
					{noZ ? ` · Z n/a on ${noZ}. That says nothing about the position's size.` : ""}
					{feed.canLoadMore ? (
						<button type="button" onClick={feed.loadMore} disabled={feed.loadingMore} className="ml-2 border border-line px-1.5 text-fg hover:border-up/50 hover:text-up">
							{feed.loadingMore ? "loading" : "load 200 more"}
						</button>
					) : null}
				</GridNote>
			</Panel>
		</View>
	);
}
